import { useEffect, useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

export function ResponsibleTeamPickerComponent(props: {
  formData?: string;
  onChange: (value: string) => void;
  required?: boolean;
  rawErrors?: string[];
  schema?: { title?: string; description?: string };
  idSchema?: { $id: string };
}) {
  const catalog = useApi(catalogApiRef);
  const identity = useApi(identityApiRef);
  const [groups, setGroups] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [noMembership, setNoMembership] = useState(false);
  const latest = useRef(props);
  latest.current = props;

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [result, user] = await Promise.all([
          catalog.getEntities({ filter: { kind: 'Group' } }),
          identity.getBackstageIdentity(),
        ]);
        if (!active) return;
        setGroups(result.items);
        const memberships = result.items.filter(group =>
          group.relations?.some(
            relation =>
              relation.type === 'hasMember' &&
              relation.targetRef === user.userEntityRef,
          ),
        );
        setNoMembership(memberships.length === 0);
        if (!latest.current.formData && memberships.length === 1) {
          latest.current.onChange(stringifyEntityRef(memberships[0]));
        }
      } catch (e) {
        if (active)
          setError(e instanceof Error ? e.message : 'Failed to load teams');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [catalog, identity]);

  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && noMembership && (
        <Alert severity="info">
          Your signed-in account has no group membership in Catalog. Select the
          responsible team manually.
        </Alert>
      )}
      <Autocomplete
        options={groups}
        loading={loading}
        value={
          groups.find(group => stringifyEntityRef(group) === props.formData) ||
          null
        }
        getOptionLabel={group =>
          `${group.metadata.title || group.metadata.name} (${stringifyEntityRef(
            group,
          )})`
        }
        getOptionSelected={(option, value) =>
          stringifyEntityRef(option) === stringifyEntityRef(value)
        }
        onChange={(_, group) =>
          props.onChange(group ? stringifyEntityRef(group) : '')
        }
        renderInput={params => (
          <TextField
            {...params}
            id={props.idSchema?.$id}
            label={props.schema?.title || 'Responsible Team'}
            required={props.required}
            error={Boolean(props.rawErrors?.length)}
            helperText={props.rawErrors?.[0] || props.schema?.description}
            variant="outlined"
          />
        )}
      />
    </>
  );
}

export const ResponsibleTeamPicker = createFormField({
  name: 'ResponsibleTeamPicker',
  component: ResponsibleTeamPickerComponent as any,
});
